import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { QuizHeader } from "../quiz-header"
import { isFilled } from "./step-contract"
import type { StepComponentProps } from "./step-contract"

/**
 * Frequency question (Steps 16–19): Never / Sometimes / Often radio rows with
 * insight copy under the selected option, and a Continue that does not
 * auto-advance. Owns its header because the per-type single-choice override
 * auto-advances.
 */
export function AuraFrequencyStep({
  step,
  value,
  onChange,
  onAdvance,
  onBack,
  countedCurrent,
  countedTotal,
}: StepComponentProps) {
  const selected = typeof value === "string" ? value : undefined
  const canProceed = isFilled(selected)

  return (
    <div className="flex flex-1 flex-col bg-dark-base text-purple-light" data-slot="aura-frequency-step">
      <QuizHeader
        current={countedCurrent}
        total={countedTotal}
        showBack
        onBack={onBack}
      />

      <div className="flex flex-1 flex-col px-4 pt-8">
        <h1 className="text-center font-display text-2xl font-medium text-purple-light">
          {step.title}
        </h1>

        <div className="mt-8 flex flex-col gap-2">
          {(step.options ?? []).map((option) => {
            const checked = selected === option.value
            return (
              <div key={option.value} className="flex flex-col gap-2">
                <button
                  type="button"
                  onClick={() => onChange(option.value)}
                  className={cn(
                    "flex h-14 w-full items-center justify-between gap-3 rounded-xl border-2 px-4 text-left backdrop-blur-[15px] transition-colors",
                    checked
                      ? "border-main-purple bg-main-purple/10 text-purple-light"
                      : "border-transparent bg-purple-light/10 text-purple-light hover:bg-purple-light/15",
                  )}
                >
                  <span className="flex items-center gap-3 text-base font-semibold">
                    {option.icon && (
                      <span className="flex size-8 shrink-0 items-center justify-center text-[32px] leading-none">
                        {option.icon}
                      </span>
                    )}
                    {option.label}
                  </span>
                  <span
                    className={cn(
                      "flex size-6 shrink-0 items-center justify-center rounded-full",
                      checked
                        ? "bg-gradient-to-b from-peach to-purple-accent"
                        : "border border-purple-light/20",
                    )}
                  >
                    {checked && (
                      <svg
                        width="11"
                        height="8"
                        viewBox="0 0 11 8"
                        fill="none"
                        aria-hidden
                        className="text-white"
                      >
                        <path
                          d="M1 4L4 7L10 1"
                          stroke="currentColor"
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </span>
                </button>
                {checked && option.description && (
                  <p className="px-4 text-sm leading-snug text-purple-light/70">
                    {option.description}
                  </p>
                )}
              </div>
            )
          })}
        </div>
      </div>

      <div className="sticky bottom-0 z-20 space-y-3 bg-gradient-to-t from-dark-base from-40% to-transparent px-4 pt-10 pb-6">
        <Button variant="gradient" size="xl" onClick={onAdvance} disabled={!canProceed}>
          Continue
        </Button>
      </div>
    </div>
  )
}
